import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function TrustedCareSection() {
  const [isConsultOpen, setIsConsultOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsConsultOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const features = [
    {
      title: "Certified Healthcare Professionals",
      desc: "Our licensed doctors and partner pharmacists make sure you get the right advice and the right medicine, safely.",
      icon: "/pharmm.png",
    },
    {
      title: "Authentic Medicines",
      desc: "We source only from NAFDAC-approved suppliers, guaranteeing every drug is original and effective, no counterfeits, ever.",
      icon: "/amox.png",
    },
    {
      title: "Fast Delivery Nationwide",
      desc: "Order from anywhere in Nigeria, our express logistics partners deliver right to your doorstep, often the same day.",
      icon: "/del.png",
    },
    {
      title: "Affordable Healthcare",
      desc: "We believe quality healthcare should be accessible to all. Enjoy fair consultation fees, fair medicine prices, and periodic discounts.",
      icon: "/htt.png",
    },
  ];

  return (
    <section className="bg-[#f9fafb] py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Heading & Updated Subtitle */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Why Nigerians Trust <span className="text-primary">ProseMediStore</span>
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
            We go beyond selling medicines. We deliver care, convenience and confidence at every step.
          </p>
        </motion.div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.05 }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              className="bg-white rounded-2xl p-6 shadow hover:shadow-lg text-center border border-gray-100 flex flex-col justify-between"
            >
              <div>
                <div className="flex justify-center mb-4">
                  <motion.img
                    src={item.icon}
                    alt={item.title}
                    className="w-16 h-16 object-contain"
                    initial={{ rotate: 0 }}
                    whileHover={{ rotate: 10 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Bottom Action Buttons (Dual CTA) */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          {/* Primary Dropdown Button: Consult an Expert */}
          <div className="relative inline-block text-left w-full sm:w-auto" ref={dropdownRef}>
            <button
              onClick={() => setIsConsultOpen(!isConsultOpen)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-white rounded-xl font-semibold shadow hover:bg-primary/90 transition-all"
            >
              Consult an Expert
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${
                  isConsultOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <AnimatePresence>
              {isConsultOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-full sm:w-80 bg-white border border-gray-100 rounded-2xl shadow-xl z-50 overflow-hidden text-left"
                >
                  <div className="p-2 space-y-1">
                    {/* Speak with a Doctor */}
                    <a
                      href="https://wa.me/2348162738527?text=Hello,%20I%20would%20like%20to%20speak%20with%20a%20doctor."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 hover:bg-slate-50 rounded-xl transition-colors group"
                    >
                      <span className="block font-semibold text-gray-900 group-hover:text-primary transition-colors text-sm">
                        Speak with a Doctor
                      </span>
                      <span className="block text-xs text-gray-500 mt-1 leading-snug">
                        Virtual consultation with a licensed medical doctor for assessment of your symptoms, diagnosis, treatment advice, prescriptions and referral for in-person care when needed.
                      </span>
                    </a>

                    {/* Speak with a Pharmacist */}
                    <a
                      href="https://wa.me/2348162738527?text=Hello,%20I%20would%20like%20to%20speak%20with%20a%20pharmacist."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 hover:bg-slate-50 rounded-xl transition-colors group"
                    >
                      <span className="block font-semibold text-gray-900 group-hover:text-primary transition-colors text-sm">
                        Speak with a Pharmacist
                      </span>
                      <span className="block text-xs text-gray-500 mt-1 leading-snug">
                        Professional advice from our partner pharmacists on your medicines, including correct use, dosage, side effects, drug interactions and over-the-counter options.
                      </span>
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Secondary Button: Shop Medicines */}
          <a
            href="#shop"
            className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3.5 bg-white border-2 border-primary text-primary rounded-xl font-semibold hover:bg-primary/5 transition-all text-center"
          >
            Shop Medicines
          </a>
        </motion.div>
      </div>
    </section>
  );
}